import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { useIsDesktop } from "../hooks/use-is-desktop";
import { cn } from "../lib/cn";

const InDrawerCtx = React.createContext(false);
const IsDesktopCtx = React.createContext(false);

const Drawer = ({
  shouldScaleBackground = false,
  disableDesktopModal = false,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root> & {
  disableDesktopModal?: boolean;
}) => {
  const isDesktop = useIsDesktop() && !disableDesktopModal;

  return (
    <IsDesktopCtx.Provider value={isDesktop}>
      <InDrawerCtx.Provider value={true}>
        {isDesktop ? (
          <DialogPrimitive.Root {...props} />
        ) : (
          <DrawerPrimitive.Root
            shouldScaleBackground={shouldScaleBackground}
            repositionInputs={false}
            {...props}
          />
        )}
      </InDrawerCtx.Provider>
    </IsDesktopCtx.Provider>
  );
};
Drawer.displayName = "Drawer";

function useInDrawer() {
  return React.useContext(InDrawerCtx);
}

function useIsDesktopDrawer() {
  return React.useContext(IsDesktopCtx);
}

const DrawerTrigger = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>
>(({ ...props }, ref) => {
  const isDesktop = useIsDesktopDrawer();

  if (isDesktop) {
    return <DialogPrimitive.Trigger ref={ref} {...props} />;
  }
  return <DrawerPrimitive.Trigger ref={ref} {...props} />;
});
DrawerTrigger.displayName = "DrawerTrigger";

const DrawerPortal = (({
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Portal>) => {
  const isDesktop = useIsDesktopDrawer();

  if (isDesktop) {
    return <DialogPrimitive.Portal {...props} />;
  }
  return <DrawerPrimitive.Portal {...props} />;
}) as typeof DrawerPrimitive.Portal;

const DrawerClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ ...props }, ref) => {
  const isDesktop = useIsDesktopDrawer();

  if (isDesktop) {
    return <DialogPrimitive.Close ref={ref} {...props} />;
  }
  return <DrawerPrimitive.Close ref={ref} {...props} />;
});
DrawerClose.displayName = "DrawerClose";

const DrawerHandle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Handle>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Handle>
>(({ ...props }, ref) => {
  const isDesktop = useIsDesktopDrawer();

  // Don't render handle on desktop
  if (isDesktop) {
    return null;
  }
  return <DrawerPrimitive.Handle ref={ref} {...props} />;
});
DrawerHandle.displayName = "DrawerHandle";

type DrawerOverlayProps = React.ComponentPropsWithoutRef<
  typeof DrawerPrimitive.Overlay
> & {
  showBackground?: boolean;
};

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  DrawerOverlayProps
>(({ className, showBackground = true, style, ...props }, ref) => {
  const isDesktop = useIsDesktopDrawer();

  if (isDesktop) {
    return (
      <DialogPrimitive.Overlay
        ref={ref}
        className={cn("fixed inset-0 z-50 bg-black-2/75", className)}
        {...props}
      />
    );
  }

  const overlayStyle = { ...style, viewTransitionName: "drawer-overlay" };

  return (
    <DrawerPrimitive.Overlay
      ref={ref}
      data-auth-drawer
      className={cn(
        "fixed inset-0 z-50",
        showBackground && "bg-black-2/75",
        className,
      )}
      style={overlayStyle}
      {...props}
    />
  );
});
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

type DrawerContentProps = React.ComponentPropsWithoutRef<
  typeof DrawerPrimitive.Content
> & {
  hideHandle?: boolean;
  showOverlayBackground?: boolean;
  childrenClassName?: string;
  childrenClassname?: string;
};

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(
  (
    {
      className,
      children,
      hideHandle = false,
      showOverlayBackground = true,
      style,
      childrenClassName,
      childrenClassname,
      ...props
    },
    ref,
  ) => {
    const isDesktop = useIsDesktopDrawer();

    if (isDesktop) {
      return (
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black-2/75" />
          <DialogPrimitive.Content
            ref={ref}
            className={cn(
              "fixed left-[50%] top-[50%] z-50 grid w-[560px] max-h-[90vh] gap-0 border-none bg-twi-gray-900 text-white shadow-lg translate-x-[-50%] translate-y-[-50%] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-4xl",
              className,
            )}
            data-testid="drawer-content"
            style={style}
            {...props}
          >
            <div
              className={cn(
                "overflow-y-auto max-h-full p-6",
                childrenClassName,
                childrenClassname,
              )}
            >
              {children}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      );
    }

    const contentStyle = { ...style, viewTransitionName: "drawer-sheet" };

    return (
      <DrawerPortal>
        <DrawerOverlay showBackground={showOverlayBackground} />
        <DrawerPrimitive.Content
          ref={ref}
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-4xl bg-black-2",
            className,
          )}
          data-testid="drawer-content"
          data-auth-drawer
          style={contentStyle}
          {...props}
        >
          {!hideHandle ? (
            <DrawerHandle
              aria-hidden="true"
              className="mx-auto mt-4 h-1 w-full rounded-full"
            />
          ) : null}
          <div
            className={cn("px-5 pt-6", childrenClassName, childrenClassname)}
          >
            {children}
          </div>
        </DrawerPrimitive.Content>
      </DrawerPortal>
    );
  },
);
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => {
  const isDesktop = useIsDesktopDrawer();

  if (isDesktop) {
    return (
      <DialogPrimitive.Title
        ref={ref}
        className={cn(
          "text-lg font-semibold leading-none tracking-tight",
          className,
        )}
        {...props}
      />
    );
  }

  return (
    <DrawerPrimitive.Title
      ref={ref}
      className={cn(
        "font-semibold text-lg leading-none tracking-tight",
        className,
      )}
      {...props}
    />
  );
});
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => {
  const isDesktop = useIsDesktopDrawer();

  if (isDesktop) {
    return (
      <DialogPrimitive.Description
        ref={ref}
        className={cn("text-sm text-muted-foreground", className)}
        {...props}
      />
    );
  }

  return (
    <DrawerPrimitive.Description
      ref={ref}
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
});
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

const DrawerLogoIcon = React.forwardRef<
  SVGSVGElement,
  React.ComponentPropsWithoutRef<"svg">
>(({ "aria-hidden": ariaHidden = true, ...props }, ref) => {
  const clipPathId = React.useId();
  const maskId = React.useId();

  return (
    <svg
      ref={ref}
      width="96"
      height="96"
      viewBox="0 0 96 96"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={ariaHidden}
      {...props}
    >
      <title>Worship Devo logo</title>
      <g clipPath={`url(#${clipPathId})`}>
        <mask
          id={maskId}
          style={{ maskType: "luminance" }}
          maskUnits="userSpaceOnUse"
          x="2"
          y="2"
          width="92"
          height="92"
        >
          <path d="M94 2H2V94H94V2Z" fill="white" />
        </mask>
        <g mask={`url(#${maskId})`}>
          <path
            d="M40.7835 48.2275C36.8855 51.4638 33.222 54.5909 29.4606 57.5953C26.7474 59.7625 24.8931 59.4669 23.0053 56.6078C20.5482 52.8864 18.2416 49.0657 15.8005 45.3332C14.5302 43.3911 14.5316 41.7633 16.1985 39.9776C25.4916 30.0221 34.6713 19.9599 44.0394 10.0759C44.9895 9.0737 46.977 8.49624 48.4152 8.6064C57.5425 9.30538 66.6598 10.1742 75.7558 11.202C77.1637 11.3611 79.4177 12.482 79.5545 13.4121C79.7492 14.7343 78.7075 16.7226 77.5679 17.6825C65.442 27.8951 53.1922 37.9603 40.7835 48.2275Z"
            fill="white"
          />
          <path
            d="M68.8999 67.8876C63.3763 73.8339 58.1117 79.6851 52.6107 85.3051C51.4604 86.4799 49.3944 87.4981 47.824 87.4005C38.5706 86.8271 29.3297 85.9896 20.1102 85.0018C18.7234 84.8534 16.7534 83.6939 16.3687 82.5567C16.0212 81.5297 17.1252 79.476 18.1861 78.5882C34.1289 65.2482 50.162 52.0161 66.1964 38.7859C69.1732 36.3298 71.0892 36.6233 73.1369 39.7737C75.5037 43.4153 77.7335 47.1475 80.1531 50.753C81.5711 52.8656 81.4162 54.5274 79.6324 56.3646C76.0345 60.0701 72.5858 63.9197 68.8999 67.8876Z"
            fill="white"
          />
        </g>
      </g>
      <defs>
        <clipPath id={clipPathId}>
          <rect
            width="92"
            height="92"
            fill="white"
            transform="translate(2 2)"
          />
        </clipPath>
      </defs>
    </svg>
  );
});
DrawerLogoIcon.displayName = "DrawerLogoIcon";

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHandle,
  DrawerHeader,
  DrawerLogoIcon,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
  useInDrawer,
  useIsDesktop,
};
